import { useEffect, useRef, useState } from "preact/hooks"
import type { User } from "../types"
import { login, signup } from "./lib/api"
import { SiteFooter } from "./SiteFooter"

interface LoginProps {
	mode: "login" | "signup"
	onAuthenticated: (user: User) => void
}

export function Login({ mode, onAuthenticated }: LoginProps) {
	// Login/signup remount on every switch (keyed by mode in App), so focusing
	// on mount is enough — no need to watch mode.
	const emailRef = useRef<HTMLInputElement>(null)
	useEffect(() => {
		emailRef.current?.focus()
	}, [])

	const [email, setEmail] = useState("")
	const [username, setUsername] = useState("")
	const [password, setPassword] = useState("")
	// Carried from a friend's invite link (?invite=TOKEN). Signup no longer
	// needs one, so there is no field to type it into — all it does is make
	// the new account friends with whoever sent it. It is still *validated*
	// though: a stale or mistyped token fails the whole signup, which is why
	// the banner below can drop it. Without that escape hatch a dead link in
	// the URL would block signing up entirely, with nothing on screen to
	// edit.
	const [inviteToken, setInviteToken] = useState(
		() => new URLSearchParams(window.location.search).get("invite") ?? "",
	)

	function dropInvite() {
		setInviteToken("")
		setError("")
		// Take it out of the URL too, so a reload does not bring it back.
		const url = new URL(window.location.href)
		url.searchParams.delete("invite")
		window.history.replaceState({}, "", url.pathname + url.search)
	}
	const [error, setError] = useState("")
	const [loading, setLoading] = useState(false)
	// Signup ends here, not in the app: it creates no session, because the
	// response has to look the same whether or not the address was already
	// registered (see signup() in api.ts). So the form's success state is
	// this panel, and the way in is the emailed link.
	const [signedUp, setSignedUp] = useState(false)

	async function handleSubmit(e: Event) {
		e.preventDefault()
		setError("")
		setLoading(true)
		try {
			if (mode === "login") {
				onAuthenticated(await login(email, password))
			} else {
				await signup(email, username, password, inviteToken)
				setSignedUp(true)
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : "Something went wrong")
		} finally {
			setLoading(false)
		}
	}

	return (
		// Two wrappers on purpose. The outer one is the scroll box — h-100, not
		// vh-100: the shell is already 100dvh (styles.css), and 100vh on a
		// phone is the viewport with the browser chrome retracted, i.e. taller
		// than what you can see. With body overflow:hidden that put the mode
		// switch link and the footer below the fold with no way to scroll to
		// them, worst on the taller signup form and worse still with the
		// keyboard up. The inner one is min-height:100% so the column is
		// centred while it fits and the box simply grows — and scrolls — when
		// it does not. Centring directly on a fixed-height overflow box would
		// clip the top of a too-tall column out of scroll reach instead.
		<div class="h-100 overflow-auto">
			<div class="flex center jc-center py-3" style="min-height: 100%;">
				<div class="stack gap-4 p-4" style="width: 100%; max-width: 24rem;">
					<div class="text-center">
						<span class="inline-flex center gap-2">
							<img src="/static/brand/logo.svg" alt="" width="30" height="30" />
							<span class="brand-name">Quizfish</span>
						</span>
					</div>

					<p class="muted text-center">
						{mode === "login"
							? "Log in to play"
							: "Create an account to play — we'll email you a link to verify it"}
					</p>

					{signedUp ? (
						<div class="stack gap-3">
							<div class="alert success">
								Check your email. We sent a link to <strong>{email}</strong> —
								open it and your account is ready.
							</div>
							<p class="muted small">
								Nothing in your inbox after a minute? Look in spam, or sign up
								again to get a fresh link.
							</p>
							<a href="/login" class="btn primary w-100">
								Log in
							</a>
						</div>
					) : (
						<form onSubmit={handleSubmit} class="stack gap-3">
							<div class="field">
								<label for="email">Email</label>
								<input
									id="email"
									ref={emailRef}
									type="email"
									autocomplete="email"
									required
									value={email}
									onInput={(e) =>
										setEmail((e.target as HTMLInputElement).value)
									}
								/>
							</div>

							{mode === "signup" && (
								<>
									<div class="field">
										<label for="username">Username</label>
										<input
											id="username"
											type="text"
											autocomplete="username"
											pattern="[a-z0-9_]{3,20}"
											title="3-20 characters: lowercase letters, numbers, underscore"
											required
											value={username}
											onInput={(e) =>
												setUsername((e.target as HTMLInputElement).value)
											}
										/>
									</div>

									{inviteToken !== "" && (
										<div class="alert info stack gap-2">
											<p>
												You're signing up from a friend's invite — you'll be
												friends as soon as you're in. If it's not yours, or the
												link has expired, you can still sign up.
											</p>
											<button
												type="button"
												class="link self-start"
												onClick={dropInvite}
											>
												Sign up without the invite
											</button>
										</div>
									)}
								</>
							)}

							<div class="field">
								<label for="password">Password</label>
								<input
									id="password"
									type="password"
									autocomplete={
										mode === "login" ? "current-password" : "new-password"
									}
									// Mirrors minPasswordLength in auth.go. Only on signup:
									// an older account may have a shorter password and must
									// still be able to log in with it.
									minLength={mode === "signup" ? 6 : undefined}
									required
									value={password}
									onInput={(e) =>
										setPassword((e.target as HTMLInputElement).value)
									}
								/>
								{mode === "signup" ? (
									<div class="hint">At least 6 characters.</div>
								) : (
									// Sits under the field it is about, where someone
									// staring at a password they cannot remember is
									// already looking.
									<div class="hint">
										<a href="/forgot">Forgot your password?</a>
									</div>
								)}
							</div>

							{error && <div class="alert danger">{error}</div>}

							<button type="submit" class="primary w-100" disabled={loading}>
								{loading
									? "Please wait…"
									: mode === "login"
										? "Log in"
										: "Sign up"}
							</button>
						</form>
					)}

					{/* The success panel carries its own Log in button; the switch
					    link below would be a second one saying the same thing. */}
					{!signedUp && (
						<div class="text-center">
							{mode === "login" ? (
								<a class="small" href="/signup">
									Need an account? Sign up
								</a>
							) : (
								<a class="small" href="/login">
									Already have an account? Log in
								</a>
							)}
						</div>
					)}

					<SiteFooter />
				</div>
			</div>
		</div>
	)
}
